/**
 * Оптимістична зміна (UX-17; states.md, розд. 2–3) — один механізм для всіх екранів:
 * 1. `apply` змінює кеш синхронно в обробнику дії, до мережі (INP); читання цих ключів, що
 *    були в дорозі, скасовуються, щоб стара відповідь не переписала зміну;
 * 2. `send` іде в черзі області: зміни одного запису (`scope`) виконуються по черзі, кожна
 *    після відповіді попередньої — без хибних конфліктів власних змін;
 * 3. відмова повертає кеш без запиту і показує тост «Не вдалося …» з «Ще раз». «Ще раз» робить
 *    ту саму зміну тим самим `send`, тобто з тим самим ключем дії (UX-18): сервер, що вже
 *    виконав її, поверне результат, а не виконає вдруге. Свій показ відмови — `onError`.
 * Офлайн зміна не робиться зовсім (UX-29): кеш не чіпаємо, запиту немає; тост з поясненням або
 * `onError` з мережевою помилкою, якщо екран показує відмову сам.
 *
 * Відкат: ключ повертається до знімка, лише якщо в кеші досі те, що поклала ця зміна. Якщо
 * після неї ключ уже змінив хтось інший (наступна оптимістична зміна, відповідь сервера, SSE),
 * знімок застарів: повернути його — стерти чужу зміну, тож ключ перечитується з сервера.
 *
 * Що оптимістично, а що з очікуванням відповіді — таблиця UX-17 (docs/dev/web-requests.md).
 */
import { hashKey, onlineManager, type QueryClient, type QueryKey } from '@tanstack/react-query';
import { states as t } from '../i18n/uk/states.ts';
import { toast } from '../ui/notify/store.ts';
import { ApiProblem } from './api.ts';
import { busyMessage } from './busy.ts';

export interface OptimisticChange<T> {
  /** Область черги: зміни з тією самою областю (`record:<сутність>:<id>`) ідуть по черзі. */
  scope: string;
  /** Ключі (префікси) кешу, які змінює `apply`: знімок до неї, відкат — до знімка. */
  keys: readonly QueryKey[];
  /** Синхронна зміна кешу. */
  apply: () => void;
  /** Запит. Той самий `send` повторює «Ще раз», тож ключ дії він бере із замикання. */
  send: () => Promise<T>;
  onSuccess?: (result: T) => void;
  /** Відмова, кеш уже повернуто. Без `onError` — тост `failText` з «Ще раз» (`retry`). */
  onError?: (error: unknown, retry: () => void) => void;
  /** Початок тексту тосту відмови: «Не вдалося зберегти». */
  failText?: string;
}

const queues = new Map<string, Promise<unknown>>();

/** Наступний у черзі області: стартує після попереднього, хоч би чим той завершився. */
function enqueue<T>(scope: string, send: () => Promise<T>): Promise<T> {
  const previous = queues.get(scope) ?? Promise.resolve();
  const run = previous.then(send, send);
  const settled = run.then(
    () => undefined,
    () => undefined,
  );
  queues.set(scope, settled);
  void settled.then(() => {
    if (queues.get(scope) === settled) queues.delete(scope);
  });
  return run;
}

/**
 * Для тестів: дочекатися, доки спорожніють усі черги областей. Зміна, що чекає в черзі, ще не
 * почала запит, тож «дочекатися запитів у дорозі» її не бачить і вона стартувала б у наступному
 * тесті (рев'ю QA #289, Must 2).
 */
export async function settleOptimistic(): Promise<void> {
  while (queues.size > 0) await Promise.all([...queues.values()]);
}

/** «Не вдалося зберегти: немає зв'язку з сервером» — причина словами, без тексту сервера. */
export function failMessage(base: string, error: unknown): string {
  const busy = busyMessage(error);
  if (busy) return `${base}. ${busy}`;
  const status = (error as { status?: unknown } | null)?.status;
  if (status === 0) return `${base}: ${t.changeFailedNetwork}`;
  if (status === 403) return `${base}: ${t.changeFailedForbidden}`;
  return base;
}

/**
 * Зробити зміну. `true` — сервер прийняв (`onSuccess` уже відпрацював), `false` — відмова
 * (кеш повернуто, `onError` чи тост показано) або офлайн (кеш не чіпали).
 */
export function runOptimistic<T>(
  client: QueryClient,
  change: OptimisticChange<T>,
): Promise<boolean> {
  if (!onlineManager.isOnline()) {
    // Кеш не чіпали й нічого не слали; свій показ відмови (картка: «Не збережено — Ще раз») —
    // через `onError` з мережевою помилкою, як і для обриву в дорозі.
    const retry = () => void runOptimistic(client, change);
    if (change.onError) change.onError(new ApiProblem(0, 'network'), retry);
    else toast({ key: change.scope, text: t.changeOffline, tone: 'alert' });
    return Promise.resolve(false);
  }
  const filters = change.keys.map((queryKey) => ({ queryKey }));
  const before = filters.flatMap((f) => client.getQueriesData(f));
  for (const f of filters) void client.cancelQueries(f);
  change.apply();
  const mine = new Map(
    filters.flatMap((f) => client.getQueriesData(f)).map(([k, d]) => [hashKey(k), d]),
  );

  const rollback = () => {
    for (const [queryKey, data] of before) {
      if (client.getQueryData(queryKey) === mine.get(hashKey(queryKey))) {
        client.setQueryData(queryKey, data);
      } else {
        void client.invalidateQueries({ queryKey, exact: true });
      }
    }
  };

  return enqueue(change.scope, change.send).then(
    (result) => {
      change.onSuccess?.(result);
      return true;
    },
    (error: unknown) => {
      rollback();
      const retry = () => void runOptimistic(client, change);
      if (change.onError) change.onError(error, retry);
      else {
        toast({
          key: change.scope,
          text: failMessage(change.failText ?? t.changeFailed, error),
          tone: 'alert',
          action: { label: t.changeAgain, onAction: retry },
        });
      }
      return false;
    },
  );
}
